import { log } from 'embeddables/src/Log'
import { EmbeddableEvent, ViewFinishedEvent } from 'embeddables/src/types/Events'
import { EmbedMessageSettings } from 'embeddables/src/types/Settings'
import PejerBase from 'pejer/src'
import { PejerState } from 'pejer/src/types/base'
import { ApiResponse, ErrorObject } from 'pejer/src/types/types'
import { reactive, ref } from 'vue'
import { Router } from 'vue-router'
import { baseConfig } from './baseConfig'
import { config } from './config'
import { LoadingReason, LoadingReasons } from './Helpers/LoadingReason'
import { RouteHandler } from './Helpers/RouteHandler'
import { InvoicesEmbed } from './tables/Invoices'
import { Company } from './types/Proff'
import { ServerOptions } from './types/ServerOptions'

export type MissingDirectRemittanceInfo = {
  bankEmail: string
  bankContactPerson: string
  textOnAccountStatement: string
}

export class PejerEmbeddables extends PejerBase<
  typeof config.schema.tables,
  typeof baseConfig.urls
> {
  globalLoading = new LoadingReason([LoadingReasons.embedToken])

  headers = reactive<Record<string, string>>({})
  parentOrigin = ref<string | null>(null)
  iframeId = ref<string | null>(null)
  tokenExpired = ref(false)
  settings = reactive<EmbedMessageSettings>({})
  routeHandler: RouteHandler
  embeddablesVersion: string | null = null

  serverOptions = ref<ServerOptions | null>(null)

  constructor(state: PejerState, router: Router) {
    super(state)
    this.routeHandler = new RouteHandler(this, router)
  }

  updateSettings(settings: EmbedMessageSettings) {
    this.settings = settings
    this.routeHandler.setDisplayNavigationSettings(settings.navbar)
  }

  setConfig(newConfig: typeof baseConfig) {
    super.setConfig(newConfig)
    return this
  }

  async setEmbedToken(token: string) {
    // const currentTime = new Date().getTime()
    // const updatedTime = new Date(currentTime + 6 * 60 * 60 * 1000)
    // Cookies.set(this.config.cookieNames.embedToken, token, {
    //   expires: updatedTime, // expires in 6 hours
    //   sameSite: 'strict',
    // })
    this.headers[this.config.cookieNames.embedToken] = token
    try {
      const serverOptions = await this.fetch(this.urls.options.get)
      if ('error' in serverOptions === false) this.serverOptions.value = serverOptions.data
    } catch (e) {
      log.error('Failed to fetch server options', e)
    }
    this.tokenExpired.value = false
    this.globalLoading.removeReason(LoadingReasons.embedToken)
  }

  onUnauthorized(res: ErrorObject) {
    const _res = super.onUnauthorized(res)
    // We want to make sure that the unauthorized event is because of the token
    if (!res.error.includes('Embed token') && !res.error.includes('x-embed-token')) return _res
    // We want to make sure that we only send the tokenExpired event once
    if (this.tokenExpired.value) return _res
    if (!this.settings.showInvalidTokenError)
      this.globalLoading.addReason(LoadingReasons.embedToken)

    log.warn('Embed token has expired')
    this.tokenExpired.value = true
    this.postToParent('token.expired')

    return _res
  }

  postToParent<T extends EmbeddableEvent['type']>(
    type: T,
    data?: Extract<EmbeddableEvent, { type: T }>['data']
  ) {
    if (type === 'view.finished') {
      const ev = data as ViewFinishedEvent['data']
      if (ev.status === 'succeeded')
        this.postToParent(ev.triggeringEvent.type, ev.triggeringEvent.data)
    }

    window.parent.postMessage(
      {
        type,
        data: data || {},
        id: this.iframeId.value,
      },
      '*'
    )
  }

  isLoggedIn() {
    return true
  }

  getHeaders() {
    const embedToken = this.headers[this.config.cookieNames.embedToken]
    return {
      [this.config.cookieNames.embedToken]: embedToken || '',
    }
  }

  async getOnboardingLink(connectedAccountID: string) {
    return await this.fetch(this.urls.onboarding.getLink, {
      pathArgs: {
        id: connectedAccountID,
      },
      body: {
        redirectUrl: this.parentOrigin.value,
      },
    })
  }

  async confirmNetsDealSent(connectedAccountID: string) {
    return await this.fetch(this.urls.connectedAccounts.confirmDirectRemittance, {
      pathArgs: {
        id: connectedAccountID,
      },
    })
  }

  async getDirectRemittancePDF(connectedAccountID: string, body: MissingDirectRemittanceInfo) {
    return await this.fetch(this.urls.connectedAccounts.getDirectRemittancePDF, {
      pathArgs: {
        id: connectedAccountID,
      },
      body: {
        ...body,
      },
    })
  }

  async saveDirectRemittance(connectedAccountID: string, body: MissingDirectRemittanceInfo) {
    return await this.fetch(this.urls.connectedAccounts.saveDirectRemittancePDF, {
      pathArgs: {
        id: connectedAccountID,
      },
      body: {
        ...body,
      },
    })
  }

  async getProffData(query: { orgID: string; countryCode: string }): Promise<ApiResponse<Company>> {
    return await this.fetch(this.urls.onboarding.getProffData, {
      body: query,
    })
  }

  async createConnectedAccount(values) {
    const res = await this.fetch(this.urls.connectedAccounts.create, {
      body: values,
    })

    if ('error' in res) return res

    this.postToParent('connectedAccount.created', {
      connectedAccountId: res.data,
    })
    return res
  }

  async AdjustOrRefundInvoice(
    paymentReferenceID: string,
    values: InvoicesEmbed.AdjustOrRefundPaymentRequest
  ): Promise<ApiResponse<string>> {
    const res = await this.fetch(this.urls.invoices.adjustOrRefund, {
      pathArgs: {
        id: paymentReferenceID,
      },
      body: values,
    })
    if ('error' in res) return res

    this.postToParent('payment.invoice.adjusted', {
      paymentReferenceId: paymentReferenceID,
    })
    return res
  }

  async getPaymentVoucher(payoutId: string) {
    return await this.fetch(this.urls.payouts.getPaymentVoucher, {
      pathArgs: {
        id: payoutId,
      },
    })
  }
}
